import {
  betStore,
  popupStore,
  userInfoStore,
  roundInfoStore,
  gameResultStore,
  gameConfigStore,
  notificationStore,
  profileAvatarStore,
  loadingStore,
} from '@pascal/pascal-core/shared/core/store';
import {
  BetStatus,
  RoundStatus,
  applicationStore,
  parseNumberByPrecision,
} from '@pascal/pascal-core';
import { freeBetStore } from '@pascal/pascal-core/shared/core/store/freeBetStore';
import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';

import { worker } from '../index';
import { gameStore } from '../store/gameStore';
import { doBetStore } from '../store/doBetStore';
import { marketsStore } from '../store/marketsStore';
import { gameLiveConnection } from './GameConnection';
import { CardTypes, CardTypesReverse } from '../enums';
import { mainHiLoParser } from '../helpers/supporters';
import { liveConnection } from '../core/liveConnection';
import { soundService, soundTypes } from './soundService';
import { statisticsDataStore } from '../store/statisticsStore';
import { convertObjectToArray } from '../helpers/convertObjectToArray';
import { parseDataFromLiveBets } from '../helpers';

let timeOutId;

class GameService {
  onGetInitialStateResult({
    limits,
    results,
    avatarId,
    roundInfo,
    currentBets,
    partnerWageringOdd,
    gameConfiguration: { markets },
  }) {
    const { Started, Finished, OnFinish } = RoundStatus;
    const { status, second, roundId } = roundInfo;
    const { isDemo } = applicationStore;
    const { isSoundOn } = userSettingsStore;
    const { userId } = userInfoStore;

    // userInfoStore.setUserInfo({
    //   rate,
    //   balance,
    //   precision,
    //   currencyCode,
    // });

    let nextHiOrLo;
    marketsStore.setupMarketsDataByType(markets);
    // if (underMaintenance) {
    //   const { startDate, notifySeconds } = underMaintenance;
    //   popupStore.startUnderMaintenanceFunctional({ startDate, notifySeconds });
    // }

    gameStore.setGameState({
      status,
      second,
      isInitialLoaded: true,
    });
    gameStore.setGameAnimationDuration(1500);

    if (currentBets && currentBets?.length) {
      betStore.setLiveBets(currentBets);
      currentBets.forEach((bet) => {
        if (+bet.userId === +userId) {
          doBetStore.updatedReBetData({ ...bet, roundId, betData: bet.betData.markets[0] });
        }
      });
    }
    if (status === Started) {
      const isBetClosed = second < 0;
      if (isBetClosed) {
        marketsStore.setActiveButton(true);
        gameStore.setIsBetsClosed(true);
      }
    } else if (status === Finished || status === OnFinish) {
      const currentTotalWinData = currentBets
        ?.filter((item) => +item?.userId === +userId && item.status === BetStatus.Win)
        ?.reduce((acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2), 0);
      if (currentTotalWinData) {
        isSoundOn && soundService.playWin();
        doBetStore.setWinAmount(currentTotalWinData);
        gameLiveConnection.getBalance();
        currentTotalWinData.forEach((item) => {
          if (+item?.userId === +userId && item.status === BetStatus.Win) {
            freeBetStore.freeBetTotalWinCalculator(item);
          }
        });
      }
      if (!isDemo) {
        gameLiveConnection.getBalance();
      }
    }

    const filteredData = results.filter((el) => {
      if (el.status === RoundStatus.Finished) {
        return true;
      }

      if (el.status === RoundStatus.OnFinish) {
        nextHiOrLo = el;
      }
      return false;
    });
    const hiLoCurrentData = filteredData[0]?.roundData;

    if (hiLoCurrentData?.resultCard === CardTypesReverse.joker) {
      marketsStore.setHiOrLoMarket(
        mainHiLoParser(hiLoCurrentData.previousCard, marketsStore.hiLoMarketsData)
      );
    } else {
      marketsStore.setHiOrLoMarket(
        mainHiLoParser(hiLoCurrentData?.resultCard, marketsStore.hiLoMarketsData)
      );
    }

    if (nextHiOrLo !== undefined) {
      if (nextHiOrLo?.roundData?.resultCard === CardTypesReverse.joker) {
        marketsStore.setHiOrLoMarket(
          mainHiLoParser(nextHiOrLo?.roundData.previousCard, marketsStore.hiLoMarketsData)
        );
      } else {
        marketsStore.setHiOrLoMarket(
          mainHiLoParser(nextHiOrLo?.roundData?.resultCard, marketsStore.hiLoMarketsData)
        );
      }
    }
    const latestResults = results
      .filter((el) => {
        return el.status === RoundStatus.Finished;
      })
      .map((el) => {
        return {
          card: CardTypes[el?.roundData?.resultCard],
          id: el?.roundId,
          isRotated: true,
          anim: false,
        };
      });

    if (partnerWageringOdd) {
      gameStore.setPartnerWageringOdd(partnerWageringOdd);
    }

    doBetStore.setBetAmount(
      freeBetStore?.activeFreeBet.id
        ? freeBetStore?.activeFreeBet?.amount
        : limits.defaultBetAmount || limits.minBet
    );
    marketsStore.setLatestMarkets(latestResults);
    gameResultStore.setGameResults(results);
    profileAvatarStore.updateAvatarId(avatarId);
    profileAvatarStore.updateInitialAvatarId(avatarId);
    marketsStore.setActiveButton(roundInfo.second <= 0);
    roundInfoStore.setRoundInfo(roundInfo);
    gameConfigStore.setLimits(limits);
    doBetStore.setBetAmount(
      freeBetStore?.activeFreeBet.id ? freeBetStore?.activeFreeBet?.amount : limits.defaultBetAmount
    );
    gameStore.compareCardResults(latestResults);
    if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      gameStore.setTimerDuration(roundInfo.second);
      const time = Math.floor(Math.abs(roundInfo.second));
      gameStore.setTime(time);
      this.sendWorker(time);
    } else {
      this.sendWorker();
      gameStore.setTimerDuration(roundInfo.duration);
      gameStore.setTime(null);
    }

    loadingStore.updateLoading({ isGameInitialLoaded: true });
    popupStore.close();
    popupStore.toggleReconnect(false);
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const { isSoundOn } = userSettingsStore;
    if (Number(userId) === Number(+data.userId) || betStore.repliedBetData) {
      if (data.freeBetId) {
        freeBetStore.handleFreeBetDoBetResult(data.freeBetId);
      }
      if (isSoundOn) {
        soundService.playBetAccepted();
      }
      doBetStore.handleDoBetResult(data);
    }
    // 1st condition for REPLAY
    if (data.betData?.markets?.length > 1 || data.betData.length > 1) {
      (data.betData.markets || data.betData).forEach((el) =>
        betStore.updateLiveBets({ ...data, id: el.id, betData: [el], amount: el.amount })
      );
    } else {
      betStore.updateLiveBets(data);
    }
  }

  handleDoBetResultValidation(data) {
    const { responseCode } = data;
    doBetStore.setIsLoadingBetButton(false);
    doBetStore.setIsLoadingReBetButton(false);
    notificationStore.setNotificationType({ responseCode });
  }

  handleRoundStart({ data: { startedRound, results, statistics } }) {
    const { roundId } = startedRound;
    gameStore.setTimerDuration(startedRound.duration);
    roundInfoStore.setRoundId(roundId);
    this.sendWorker(startedRound.duration);
    doBetStore.setWinAmount(0);
    doBetStore.updateTotalBetsAmount(0, true);
    gameStore.setIsBetsClosed(false);
    betStore.setLiveBets([]);
    const latestResults = results
      .filter((el) => {
        return el.status === RoundStatus.Finished;
      })
      .map((el) => {
        return {
          card: CardTypes[el?.roundData?.resultCard],
          id: el?.roundId,
          isRotated: true,
          anim: false,
        };
      });
    marketsStore.setActiveButton(false);
    marketsStore.setLatestMarkets(latestResults);
    gameResultStore.setGameResults(results);
    freeBetStore.handleFreeBetEndedData();
    statisticsDataStore.setStatisticsData(convertObjectToArray(statistics));
  }

  handleRoundFin({ data }) {
    const {
      finishedRound: { roundData },
      bets,
    } = data;
    const { userId } = userInfoStore;
    const { isSoundOn } = userSettingsStore;
    const {
      hiLoMarketsData,
      selectedMarket,
      setSelectedMarket,
      setHiOrLoMarket,
      latestMarkets,
      setLatestMarkets,
    } = marketsStore;
    const { repliedBetData } = betStore;
    gameStore.setTimerDuration(roundInfoStore.duration);
    // let myBets;

    setLatestMarkets(
      latestMarkets.map((item, index) => {
        if (index === 0) {
          return {
            ...item,
            id: item?.id,
            card: CardTypes[repliedBetData ? repliedBetData.roundData.resultCard : roundData],
            isRotated: true,
            anim: false,
          };
        }

        return item;
      })
    );

    if (isSoundOn) {
      soundService.play(soundTypes.card);
    }
    if (roundData !== CardTypesReverse.joker) {
      const hiLoData = mainHiLoParser(roundData, hiLoMarketsData);
      const selectedHiLoData = hiLoData.find((item) => item.hiOrLo === selectedMarket.hiOrLo);
      if (selectedHiLoData) {
        setSelectedMarket(selectedHiLoData);
      }
      if (selectedMarket.hiOrLo !== null && hiLoData.length === 1) {
        setSelectedMarket(hiLoData[0]);
      }
      setHiOrLoMarket(hiLoData);
    }

    if (bets.length) {
      const currentTotalWinData = bets
        ?.filter((item) => +item?.userId === +userId && item.status === BetStatus.Win)
        ?.reduce((acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2), 0);

      // ||
      //   (applicationStore.isDemo && +userInfoStore.userId === item.userId)

      if (currentTotalWinData) {
        isSoundOn && soundService.playWin();
        doBetStore.setWinAmount(currentTotalWinData);
        gameLiveConnection.getBalance();
        bets.forEach((item) => {
          if (+item?.userId === +userId && item.status === BetStatus.Win) {
            freeBetStore.freeBetTotalWinCalculator(item);
          }
        });
        // myBets = bets
        //   .filter((item) => +item.userId === +userInfoStore.userId)
        //   .map((bet) => {
        //     if (bet.additionalParams.markets.length === 1) {
        //       return bet;
        //     }
        //     return bet.additionalParams.markets.map((el) => {
        //       return {
        //         ...bet,
        //         id: el.id,
        //         status: el.status,
        //         odd: el.odd,
        //         amount: el.amount,
        //         winAmount: el.winAmount,
        //         additionalParams: {
        //           markets: [
        //             {
        //               ...el,
        //             },
        //           ],
        //         },
        //       };
        //     });
        //   });
      }

      const finishLiveBets = parseDataFromLiveBets(bets);
      betStore.setLiveBets(finishLiveBets);

      const myBets = bets.filter((item) => +item.userId === +userInfoStore.userId);
      doBetStore.detectAndSetReBetData(myBets);
    }

    // const myReBetLiveBets = myBets?.find((el) => Array.isArray(el));
    //
    // const myLiveBets = myBets?.filter((el) => !Array.isArray(el));
    //
    // const otherLiveBets = bets?.filter((item) => +item.userId !== +userInfoStore.userId);
    //
    // betStore.setLiveBets([
    //   ...(myReBetLiveBets || []),
    //   ...(myLiveBets || []),
    //   ...(otherLiveBets || []),
    // ]);
  }

  handleBetsClosed() {
    marketsStore.setActiveButton(true);
    gameStore.setIsBetsClosed(true);
    doBetStore.setIsLoadingBetButton(false);

    const { repliedBetData } = betStore;
    const updatedLatestMarkets = gameResultStore.gameResults
      .filter((item) => item?.status === RoundStatus.Finished)
      .map((item) => ({
        card: CardTypes[item?.roundData?.resultCard],
        id: item?.roundId,
        isRotated: true,
        anim: false,
      }));

    const nextCardItemKey = gameResultStore?.gameResults?.find(
      (item) => item?.status === RoundStatus?.Upcoming
    )?.roundId;

    if (repliedBetData) {
      marketsStore.setLatestMarkets(
        marketsStore.latestMarkets.map((item) => ({
          ...item,
          isRotated: true,
          anim: true,
        }))
      );
    } else {
      marketsStore.setLatestMarkets([
        { ...updatedLatestMarkets[0], isRotated: true, anim: true },
        ...updatedLatestMarkets.slice(1),
      ]);
    }

    if (this.handleBetsClosedTimeout) {
      clearTimeout(this.handleBetsClosedTimeout);
    }

    this.handleBetsClosedTimeout = setTimeout(() => {
      if (repliedBetData) {
        marketsStore.setLatestMarkets([
          {
            card: '',
            isRotated: false,
            anim: false,
            id: 123,
          },
          ...marketsStore.latestMarkets.map((item) => ({
            ...item,
            isRotated: true,
            anim: false,
          })),
        ]);
      } else {
        marketsStore.setLatestMarkets([
          {
            card: '',
            isRotated: false,
            anim: false,
            id: nextCardItemKey,
          },
          { ...updatedLatestMarkets[0], isRotated: true, anim: false },
          ...updatedLatestMarkets.slice(1),
        ]);
      }
    }, 400);

    freeBetStore.handleFreeBetEndedData();
  }

  countDown({ data: time }) {
    gameStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError() {
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
